import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook } from 'lucide-react';
import { motion } from 'motion/react';

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

export const Footer = () => {
  const socialLinks = [
    { name: 'Instagram', url: 'https://www.instagram.com/caninecandyltd', icon: Instagram },
    { name: 'Facebook', url: 'https://www.facebook.com/people/Canine-Candy-Ltd/61581182363643/', icon: Facebook },
    { name: 'TikTok', url: 'https://www.tiktok.com/@canine_candy', icon: TikTokIcon }
  ];

  return (
    <footer className="relative bg-ink text-white pt-24 pb-12 rounded-t-[4rem] -mt-12 z-20">
      <div className="max-w-7xl mx-auto px-8 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-24">
          <div className="col-span-1 md:col-span-2">
            <h2 className="font-serif text-6xl md:text-8xl tracking-tight mb-12 text-primary rotate-[-2deg] leading-relaxed">
              Canine<br/>Candy
            </h2>
            <p className="font-sans text-xl text-white/80 max-w-md font-medium">
              Nostalgic treats for your furry best friend. Handcrafted with love and sprinkles.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <h2 className="font-mono text-xs uppercase text-primary mb-4 tracking-widest">Bakery</h2>
            {[
              { name: 'Shop Treats', path: '/shop' },
              { name: 'Subscribe', path: '/subscribe' },
              { name: 'Our Ingredients', path: '/ingredients' },
              { name: 'Our Story', path: '/story' },
              { name: 'Doggy Blog', path: '/blog' },
              { name: 'Treat Runner Game', path: '/treat-runner' },
              { name: 'Gift Cards', path: '/shop?category=Gift Card' }
            ].map(item => (
              <Link key={item.name} to={item.path} className="font-sans font-bold text-lg py-2 hover:text-primary transition-colors">{item.name}</Link>
            ))}
          </div>

          <div className="flex flex-col gap-4">
            <h2 className="font-mono text-xs uppercase text-primary mb-4 tracking-widest">Support</h2>
            {[
              { name: 'Shipping', path: '/shipping' },
              { name: 'FAQ', path: '/faq' },
              { name: 'Contact', path: '/contact' },
              { name: 'Privacy Policy', path: '/privacy-policy' }
            ].map(item => (
              <Link key={item.name} to={item.path} className="font-sans font-bold text-lg py-2 hover:text-primary transition-colors">{item.name}</Link>
            ))}
          </div>

          <div className="flex flex-col gap-4">
            <h2 className="font-mono text-xs uppercase text-primary mb-4 tracking-widest">Social</h2>
            <div className="flex gap-4">
              {socialLinks.map((item) => (
                <motion.a 
                  key={item.name} 
                  href={item.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="p-3 rounded-xl bg-white/5 hover:bg-primary/20 text-white hover:text-primary transition-all"
                  whileHover={{ y: -5, scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title={item.name}
                >
                  <item.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-12 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-3 bg-white/5 px-6 py-3 rounded-full">
            <span className="text-xl">🧁</span>
            <span className="font-mono text-xs uppercase tracking-widest text-white/80">Made with love in the UK</span>
          </div>

          <p className="font-mono text-xs text-white/70 font-medium">
            © 2026 CANINE CANDY LTD. ALL RIGHTS RESERVED.
          </p>
        </div>
      </div>
    </footer>
  );
};
